import { apiRequest } from './api';

// Utility functions for trade processing and reconciliation
// Extracted from StockSummary.js to be shared with Dashboard.js

// Parse option symbol to extract components
export const parseOptionSymbol = (symbol) => {
  if (!symbol) return { isOption: false, underlying: symbol };
  
  // Handle different option symbol formats
  // Descriptive formats from broker exports:
  // - FDX 12/19/25 250 PUT
  // - TSLA 01/09/2026 430.00 CALL
  // Support both 2-digit and 4-digit year and CALL/PUT words or C/P letters.
  // Also support optional $ before strike (e.g. Robinhood format)
  const descWordMatchYY = symbol.match(/^([A-Z]+)\s+(\d{2})\/(\d{2})\/(\d{2})\s+\$?(\d+(?:\.\d+)?)\s+(CALL|PUT)$/i);
  if (descWordMatchYY) {
    const [, underlying, mm, dd, yy, strikeStr, callPut] = descWordMatchYY;
    const expiration = `${yy}${mm}${dd}`; // YYMMDD
    const strike = parseFloat(strikeStr);
    const type = /call/i.test(callPut) ? 'C' : 'P';
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }
  const descWordMatchYYYY = symbol.match(/^([A-Z]+)\s+(\d{2})\/(\d{2})\/(\d{4})\s+(\d+(?:\.\d+)?)\s+(CALL|PUT)$/i);
  if (descWordMatchYYYY) {
    const [, underlying, mm, dd, yyyy, strikeStr, callPut] = descWordMatchYYYY;
    const yy = String(parseInt(yyyy, 10) % 100).padStart(2, '0');
    const expiration = `${yy}${mm}${dd}`; // YYMMDD
    const strike = parseFloat(strikeStr);
    const type = /call/i.test(callPut) ? 'C' : 'P';
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }
  // Descriptive with C/P letter suffix (older pattern): TSLA 01/09/2026 430.00 P
  const descLetterMatch = symbol.match(/^([A-Z]+)\s+(\d{2})\/(\d{2})\/(\d{4})\s+(\d+(?:\.\d+)?)\s+([CP])$/);
  if (descLetterMatch) {
    const [, underlying, mm, dd, yyyy, strikeStr, type] = descLetterMatch;
    const yy = String(parseInt(yyyy, 10) % 100).padStart(2, '0');
    const expiration = `${yy}${mm}${dd}`; // YYMMDD
    const strike = parseFloat(strikeStr);
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }
  
  // Schwab numeric format: AAPL  240119C00150000 (spaces between underlying and option details)
  const schwabMatch = symbol.match(/^([A-Z]+)\s+(\d{6})([CP])(\d{8})$/);
  if (schwabMatch) {
    const [, underlying, expiration, type, strikeStr] = schwabMatch;
    const strike = parseInt(strikeStr) / 1000; // Convert from 8-digit format
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }
  
  // Standard format: AAPL240119C00150000
  const standardMatch = symbol.match(/^([A-Z]+)(\d{6})([CP])(\d{8})$/);
  if (standardMatch) {
    const [, underlying, expiration, type, strikeStr] = standardMatch;
    const strike = parseInt(strikeStr) / 1000;
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }
  
  // Tradier format: AAPL240119C150 (shorter strike format)
  const tradierMatch = symbol.match(/^([A-Z]+)(\d{6})([CP])(\d+(?:\.\d+)?)$/);
  if (tradierMatch) {
    const [, underlying, expiration, type, strikeStr] = tradierMatch;
    const strike = parseFloat(strikeStr);
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }

  // Futures option (thinkorswim/CME symbology): /MS4EV26_P7600:XCME —
  // /ROOT<monthYY>_<P|C><strike>:<VENUE>. The embedded week/month codes can't
  // be decoded to a calendar day without per-product rules, so expiration is
  // left null; the contract root still groups legs together.
  const futOptMatch = symbol.match(/^(\/[A-Z0-9]+)[_ ]([CP])(\d+(?:\.\d+)?)(?::([A-Z]+))?$/i);
  if (futOptMatch) {
    const [, root, type, strikeStr, venue] = futOptMatch;
    return {
      isOption: true,
      isFuturesOption: true,
      underlying: root.toUpperCase(),
      expiration: null,
      type: type.toUpperCase(),
      strike: parseFloat(strikeStr),
      exchange: venue ? venue.toUpperCase() : undefined,
      originalSymbol: symbol
    };
  }

  // Not an option symbol
  return { isOption: false, underlying: symbol, originalSymbol: symbol };
};

// Canonical contract key for tag lookups — broker position symbols (padded OCC
// like "SPXW  260918P06750000") and imported trade symbols (descriptive like
// "SPXW 09/18/26 6750.00 PUT") normalize to the same key. Shared by
// StockSummary and Dashboard so tag filtering behaves identically.
export const contractTagKey = (sym) => {
  const raw = String(sym || '').trim().toUpperCase();
  if (!raw) return null;
  // Parse before stripping whitespace — descriptive formats need the spaces.
  const info = parseOptionSymbol(raw);
  if (info?.isOption && info.underlying != null && info.expiration != null && info.strike != null) {
    return `${info.underlying}|${info.expiration}|${info.type}|${info.strike}`;
  }
  return raw.replace(/\s+/g, '');
};

// Format individual option display label
export const formatOptionLabel = (trade) => {
  const optionInfo = parseOptionSymbol(trade.originalSymbol || trade.symbol);
  
  if (!optionInfo.isOption) {
    return trade.originalSymbol || trade.symbol; // Return as-is for stocks
  }
  
  const { underlying, expiration, type, strike } = optionInfo;
  
  // Format expiration from YYMMDD to MM/DD/YY
  let formattedExpiration = expiration;
  if (expiration && expiration.length === 6) {
    const year = expiration.substring(0, 2);
    const month = parseInt(expiration.substring(2, 4), 10);
    const day = parseInt(expiration.substring(4, 6), 10);
    formattedExpiration = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
  }

  // Format strike price (remove unnecessary decimals)
  const formattedStrike = strike && typeof strike === 'number' ?
    (strike % 1 === 0 ? strike.toString() : strike.toFixed(2)) : 'N/A';

  // Robinhood-style label: "CRWV 10/02/26 $95 Put"
  const optionType = type === 'C' ? 'Call' : 'Put';

  // Futures options have no decodable expiry — omit the date slot.
  if (optionInfo.isFuturesOption || !formattedExpiration) {
    return `${underlying} $${formattedStrike} ${optionType}`;
  }
  return `${underlying} ${formattedExpiration} $${formattedStrike} ${optionType}`;
};

// Distinct leg strikes for a multi-leg option group, ascending — renders as
// "7660/7655/7765/7770" next to the underlying symbol.
export const multiLegStrikes = (trades) => {
  const seen = new Set();
  (trades || []).forEach(t => {
    const info = parseOptionSymbol(t.originalSymbol || t.original_symbol || t.symbol);
    if (info?.isOption && info.strike != null) seen.add(info.strike);
  });
  return [...seen]
    .sort((a, b) => a - b)
    .map(s => (s % 1 === 0 ? s.toString() : s.toFixed(2)));
};

// Determine trade status (open/closed)
export const getTradeStatus = (trade) => {
  if (!trade || typeof trade !== 'object') return 'Open';
  const tx = String(trade.transactionType || trade.transaction_type || '').toUpperCase();
  const tt = String(trade.tradeType || trade.trade_type || '').toUpperCase();
  const ttNorm = tt.replace(/_/g, ' ');
  const pe = String(trade.positionEffect || trade.position_effect || '').toUpperCase().replace(/_/g, ' ');

  // Explicit closing events
  if (tx === 'OEXP' || tx === 'OASGN' || tx === 'ASSIGN' || tx === 'EXPIRE' || tx === 'DIVIDEND') return 'Closed';
  if (tt === 'BTC' || tt === 'STC' || tt === 'EXP' || tt === 'ASGN' || tt === 'DIVIDEND' || tt === 'DIV') return 'Closed';
  if (pe.includes('BUY TO CLOSE') || pe.includes('SELL TO CLOSE') || ttNorm === 'BUY TO CLOSE' || ttNorm === 'SELL TO CLOSE') return 'Closed';
  
  // Note: MANUAL_ASSIGN is usually an OPENING transaction for the stock, 
  // so we don't treat it as 'Closed' here unless it has a closing date.
  if (tx === 'MANUAL_ASSIGN') return 'Open';

  // Realized P/L or Dividend present indicates a closed row
  const realized = parseFloat(trade.realizedPL || trade.realized_pl || '');
  const div = parseFloat(trade.dividend || trade.dividend_amount || '');
  if (!isNaN(realized) && Math.abs(realized) > 0) return 'Closed';
  if (!isNaN(div) && Math.abs(div) > 0) return 'Closed';

  // Options: if past expiration, treat as closed (do not show expired opens as open)
  const optionInfo = parseOptionSymbol(trade.originalSymbol || trade.symbol);
  if (optionInfo.isOption && optionInfo.expiration) {
    const expYear = 2000 + parseInt(optionInfo.expiration.substring(0, 2));
    const expMonth = parseInt(optionInfo.expiration.substring(2, 4)) - 1;
    const expDay = parseInt(optionInfo.expiration.substring(4, 6));
    const expirationDate = new Date(expYear, expMonth, expDay);
    expirationDate.setHours(23, 59, 59, 999);
    if (expirationDate < new Date()) return 'Closed';
  }

  return 'Open';
};

// Client-side reconciliation to compute realized P/L on close rows and hide fully matched opening legs
export const reconcileTradesForDisplay = (trades) => {
  const rows = [...(trades || [])].sort((a, b) => {
    const da = new Date(a.tradeTimestamp || a.trade_timestamp || a.tradeDate || 0);
    const db = new Date(b.tradeTimestamp || b.trade_timestamp || b.tradeDate || 0);
    if (da.getTime() !== db.getTime()) return da - db;
    return (a.id || 0) - (b.id || 0);
  }).map(r => ({ ...r, _hideOpeningLeg: false }));

  const isOptionRow = (row) => {
    const info = parseOptionSymbol(row.originalSymbol || row.symbol || "");
    return !!(info && info.isOption);
  };
  // Lot matching must never cross accounts — the same contract held at two
  // brokers are separate positions, and mixing their lots fabricates P/L.
  const accountKey = (row) => String(row.accountId ?? row.account_id ?? row.account ?? '');
  const optionKey = (row) => {
    const acct = accountKey(row);
    const raw = row.originalSymbol || row.symbol || "";
    const info = parseOptionSymbol(raw);
    if (info && info.isOption && info.expiration && info.type && info.strike != null) {
      return `${info.underlying}|${info.expiration}|${info.type}|${info.strike}|${acct}`;
    }
    // For stocks, use a robust key that preserves class (e.g. BRK B) but normalizes format
    const normalized = raw.replace(/_/g, ' ').trim();
    const tokens = normalized.split(/\s+/).filter(Boolean);
    if (tokens.length >= 2 && /^[A-Z]$/.test(tokens[1])) {
        return `${tokens[0]}.${tokens[1]}|STOCK|${acct}`;
    }
    return `${tokens[0] || normalized}|STOCK|${acct}`;
  };
  const equityKey = (row) => `${row.symbol}|${accountKey(row)}`;
  const optionMultiplier = (row) => (isOptionRow(row) ? 100 : 1);

  const getRowDividend = (row) => parseFloat(row.dividend || row.dividend_amount || 0) || 0;

  const explicitEffect = (row) => {
    const pe = String(row.positionEffect || row.position_effect || '').toUpperCase().replace(/_/g, ' ');
    const tt = String(row.tradeType || row.trade_type || '').toUpperCase();
    const ttNorm = tt.replace(/_/g, ' ');
    const tx = String(row.transactionType || row.transaction_type || '').toUpperCase();
    if (pe.includes('SELL TO OPEN') || ttNorm === 'SELL TO OPEN') return 'STO';
    if (pe.includes('BUY TO OPEN') || ttNorm === 'BUY TO OPEN') return 'BTO';
    if (pe.includes('BUY TO CLOSE') || ttNorm === 'BUY TO CLOSE') return 'BTC';
    if (pe.includes('SELL TO CLOSE') || ttNorm === 'SELL TO CLOSE') return 'STC';
    if (tx === 'OASGN') return 'ASSIGN';
    if (tx === 'OEXP') return 'EXPIRE';
    if (tt === 'EXP') return 'EXPIRE';
    if (tt === 'STC' || tt === 'BTC' || tt === 'BTO' || tt === 'STO') return tt; // already normalized
    return null; // require inference
  };

  const inferOptionEffect = (row, key) => {
    const exp = explicitEffect(row);
    if (exp) return exp;
    
    const tt = String(row.tradeType || row.trade_type || '').toUpperCase();
    const tx = String(row.transactionType || row.transaction_type || '').toUpperCase();
    
    if (tx === 'OASGN') return 'ASSIGN';
    if (tx === 'OEXP') return 'EXPIRE';
    
    // Determine action (BUY/SELL)
    let action = 'UNKNOWN';
    const qty = parseFloat(row.quantity || 0);
    
    if (tt === 'SELL' || tt === 'STO') action = 'SELL';
    else if (tt === 'BUY' || tt === 'BTO') action = 'BUY';
    else if (qty < 0) action = 'SELL';
    else if (qty > 0) action = 'BUY';
    
    const buckets = openOptionLots.get(key);
    
    if (action === 'SELL') {
      // If we have any long lots, selling is closing (STC); otherwise opening short (STO)
      if (buckets && buckets.long && buckets.long.length > 0) return 'STC';
      return 'STO';
    }
    if (action === 'BUY') {
      // If we have any short lots, buying is closing (BTC); otherwise opening long (BTO)
      if (buckets && buckets.short && buckets.short.length > 0) return 'BTC';
      return 'BTO';
    }
    return 'UNKNOWN';
  };

  const openOptionLots = new Map(); // key -> { long: [{remaining, basisPerUnit, rowRef}], short: [{remaining, premiumPerUnit, rowRef}] }
  const openEquityLots = new Map(); // symbol -> [{remaining, basisPerUnit, rowRef}]

  const addOptionLot = (key, side, row) => {
    const qty = Math.abs(parseFloat(row.quantity || 0)) || 0;
    const mult = optionMultiplier(row);
    const units = qty * mult;
    if (!openOptionLots.has(key)) openOptionLots.set(key, { long: [], short: [] });
    const bucket = openOptionLots.get(key);
    const fees = Math.abs(parseFloat(row.fees || 0)) || 0;
    const div = getRowDividend(row);
    if (side === 'LONG') {
      const basisTotal = Math.abs(parseFloat(row.cost || 0)) + fees - div;
      bucket.long.push({ remaining: units, basisPerUnit: units > 0 ? basisTotal / units : 0, rowRef: row });
    } else {
      const premiumTotal = Math.abs(parseFloat(row.proceeds || 0)) + div;
      bucket.short.push({ remaining: units, premiumPerUnit: units > 0 ? premiumTotal / units : 0, rowRef: row });
    }
  };

  const consumeOptionClose = (key, sideToClose, row) => {
    const qty = Math.abs(parseFloat(row.quantity || 0)) || 0;
    const mult = optionMultiplier(row);
    let remaining = qty * mult;
    const buckets = openOptionLots.get(key);
    if (!buckets) return 0;
    const fifo = sideToClose === 'LONG' ? buckets.long : buckets.short;
    let realized = 0;
    const fees = Math.abs(parseFloat(row.fees || 0)) || 0;
    const div = getRowDividend(row);
    const alloc = row._lotAlloc || (row._lotAlloc = []);
    while (remaining > 0 && fifo.length > 0) {
      const lot = fifo[0];
      const take = Math.min(lot.remaining, remaining);
      let portion;
      if (sideToClose === 'LONG') {
        const proceedsPerUnit = Math.abs(parseFloat(row.proceeds || 0)) / (qty * mult || 1);
        portion = take * (proceedsPerUnit - lot.basisPerUnit);
      } else {
        const costPerUnit = Math.abs(parseFloat(row.cost || 0)) / (qty * mult || 1);
        portion = take * (lot.premiumPerUnit - costPerUnit);
      }
      realized += portion;
      // Per-lot attribution: merged close fills (several positions' shared
      // contract batched under one order id) split back out by opening order.
      alloc.push({
        orderId: lot.rowRef.orderId ?? lot.rowRef.order_id ?? null,
        qty: take / (optionMultiplier(lot.rowRef) || 1),
        realized: portion,
      });
      lot.remaining -= take;
      remaining -= take;
      if (lot.remaining <= 0) { fifo.shift(); lot.rowRef._hideOpeningLeg = true; }
    }
    realized -= fees;
    realized += div;
    if (alloc.length) alloc[alloc.length - 1].realized += div - fees;
    return realized;
  };

  const synthesizeOptionCloseFromLots = (key, sideToClose, row) => {
    const buckets = openOptionLots.get(key);
    if (!buckets) return { realized: 0, units: 0 };
    const fifo = sideToClose === 'LONG' ? buckets.long : buckets.short;
    const alloc = row ? (row._lotAlloc || (row._lotAlloc = [])) : null;
    let realized = 0;
    let totalUnits = 0;
    while (fifo.length > 0) {
      const lot = fifo.shift();
      const take = lot.remaining;
      totalUnits += take;
      const portion = sideToClose === 'LONG'
        ? -take * lot.basisPerUnit
        : take * lot.premiumPerUnit;
      realized += portion;
      if (alloc) {
        alloc.push({
          orderId: lot.rowRef.orderId ?? lot.rowRef.order_id ?? null,
          qty: take / (optionMultiplier(lot.rowRef) || 1),
          realized: portion,
        });
      }
      lot.rowRef._hideOpeningLeg = true;
    }
    return { realized, units: totalUnits };
  };

  const addEquityLot = (row) => {
    const qty = Math.abs(parseFloat(row.quantity || 0)) || 0;
    const fees = Math.abs(parseFloat(row.fees || 0)) || 0;
    const div = getRowDividend(row);
    const basisTotal = Math.abs(parseFloat(row.cost || 0)) + fees - div;
    const unitBasis = qty > 0 ? basisTotal / qty : 0;
    const lots = openEquityLots.get(equityKey(row)) || [];
    lots.push({ remaining: qty, basisPerUnit: unitBasis, rowRef: row });
    openEquityLots.set(equityKey(row), lots);
  };

  const consumeEquitySell = (row) => {
    const qty = Math.abs(parseFloat(row.quantity || 0)) || 0;
    let remaining = qty;
    const lots = openEquityLots.get(equityKey(row)) || [];
    let realized = 0;
    const fees = Math.abs(parseFloat(row.fees || 0)) || 0;
    const div = getRowDividend(row);
    const proceedsPerUnit = Math.abs(parseFloat(row.proceeds || 0)) / (qty || 1);
    const alloc = row._lotAlloc || (row._lotAlloc = []);
    while (remaining > 0 && lots.length > 0) {
      const lot = lots[0];
      const take = Math.min(lot.remaining, remaining);
      const portion = take * (proceedsPerUnit - lot.basisPerUnit);
      realized += portion;
      alloc.push({
        orderId: lot.rowRef.orderId ?? lot.rowRef.order_id ?? null,
        qty: take,
        realized: portion,
      });
      lot.remaining -= take;
      remaining -= take;
      if (lot.remaining <= 0) { lots.shift(); lot.rowRef._hideOpeningLeg = true; }
    }
    openEquityLots.set(equityKey(row), lots);
    realized -= fees;
    realized += div;
    return realized;
  };

  for (const row of rows) {
    const key = optionKey(row);

    // Use the DB realizedPL when it is non-zero; always process opening trades so we can
    // build lots for client-side FIFO. We only recompute realizedPL for closing trades
    // when the DB value is absent/zero. Note: trade fetches filtered by closing_date
    // often exclude the opening legs entirely, so client-side FIFO has no lots to
    // consume — the broker-computed realized_pl is authoritative in that case.
    const existingRealized = row.realizedPL ?? row.realized_pl;
    const ttTop = String(row.tradeType || row.trade_type || row.transactionType || row.transaction_type || '').toUpperCase();
    const isOpening = ['BUY','BTO','BUY TO OPEN','STO','SELL TO OPEN','PERSHING_HOLDING'].includes(ttTop);
    const hasDbRealized = existingRealized !== null && existingRealized !== undefined;
    if (hasDbRealized && !isOpening && parseFloat(existingRealized) !== 0) {
      // Trust the broker value but still consume FIFO lots so subsequent closes
      // and the remaining-lots synthesis don't double-count the same openings.
      if (isOptionRow(row)) {
        const e = inferOptionEffect(row, key);
        if (e === 'STC') consumeOptionClose(key, 'LONG', row);
        else if (e === 'BTC') consumeOptionClose(key, 'SHORT', row);
        else if (e === 'ASSIGN' || e === 'EXPIRE') {
          const b = openOptionLots.get(key);
          if (b) {
            const s = (b.long && b.long.length) ? 'LONG' : 'SHORT';
            synthesizeOptionCloseFromLots(key, s, row);
          }
        }
      } else if (ttTop === 'SELL') {
        consumeEquitySell(row);
        row._isClosing = true;
      }
      continue;
    }

    if (isOptionRow(row)) {
      // Option assignment is a removal event — the premium economics roll into
      // the delivered stock leg (server-side reconcile does the basis/proceeds
      // adjustment). Consume the matching open lots so the opening legs hide,
      // but leave the row's stored P/L as-is (normally 0).
      const isAssignmentRow = String(row.tradeType || row.trade_type || '').toUpperCase() === 'ASSIGNMENT';
      if (isAssignmentRow) {
        const buckets = openOptionLots.get(key);
        if (buckets && ((buckets.long && buckets.long.length) || (buckets.short && buckets.short.length))) {
          const sideToClose = (buckets.long && buckets.long.length) ? 'LONG' : 'SHORT';
          const { units } = synthesizeOptionCloseFromLots(key, sideToClose, row);
          if (units > 0) row._closedSide = sideToClose;
        }
        continue;
      }
      const e = inferOptionEffect(row, key);
      if (e === 'BTO') { addOptionLot(key, 'LONG', row); continue; }
      if (e === 'STO') { addOptionLot(key, 'SHORT', row); continue; }
      if (e === 'STC') { const realized = consumeOptionClose(key, 'LONG', row); row.realizedPL = realized; row.netPL = realized; row._closedSide = 'LONG'; continue; }
      if (e === 'BTC') { const realized = consumeOptionClose(key, 'SHORT', row); row.realizedPL = realized; row.netPL = realized; row._closedSide = 'SHORT'; continue; }
      if (e === 'ASSIGN' || e === 'EXPIRE') {
        const buckets = openOptionLots.get(key);
        if (buckets && ((buckets.long && buckets.long.length) || (buckets.short && buckets.short.length))) {
          const sideToClose = (buckets.long && buckets.long.length) ? 'LONG' : 'SHORT';
          const { realized, units } = synthesizeOptionCloseFromLots(key, sideToClose, row);
          if (units > 0) { row.realizedPL = realized; row.netPL = realized; row._closedSide = sideToClose; }
        }
        continue;
      }
    } else {
      const tt = String(row.tradeType || '').toUpperCase();
      if (tt === 'BUY') { addEquityLot(row); continue; }
      if (tt === 'SELL') { const realized = consumeEquitySell(row); row.realizedPL = realized; row.netPL = realized; row._isClosing = true; continue; }
    }
  }
  // Synthesize expiration aggregates for remaining open lots past expiration
  try {
    const makeInfoFromKey = (key) => {
      const [underlying, expiration, type, strike] = (key || '').split('|');
      const originalSymbol = `${underlying} ${expiration.substring(2,4)}/${expiration.substring(4,6)}/20${expiration.substring(0,2)} ${strike} ${type === 'C' ? 'CALL' : 'PUT'}`;
      return { underlying, expiration, type, strike: parseFloat(strike), originalSymbol };
    };

    const now = new Date();
    const synthExpAggregates = [];
    for (const [key, buckets] of openOptionLots.entries()) {
      const info = makeInfoFromKey(key);
      if (!info || !info.expiration) continue;
      const expYear = 2000 + parseInt(info.expiration.substring(0,2));
      const expMonth = parseInt(info.expiration.substring(2,4)) - 1;
      const expDay = parseInt(info.expiration.substring(4,6));
      const expirationDate = new Date(expYear, expMonth, expDay);
      expirationDate.setHours(23,59,59,999);
      if (expirationDate >= now) continue; // not yet expired

      // If either long or short lots remain, synthesize close at expiration
      const hasLong = buckets.long && buckets.long.length > 0;
      const hasShort = buckets.short && buckets.short.length > 0;
      if (!hasLong && !hasShort) continue;

      const sideToClose = hasLong ? 'LONG' : 'SHORT';
      // Capture account refs BEFORE synthesizing — the synth call drains the
      // bucket arrays, so indexing them afterwards can hit undefined.
      const acctRef = hasLong ? buckets.long[0].rowRef : buckets.short[0].rowRef;
      const { realized, units } = synthesizeOptionCloseFromLots(key, sideToClose);
      if (units <= 0) continue;

      const formattedLabel = formatOptionLabel({ originalSymbol: info.originalSymbol });
      const aggRow = {
        id: `client-exp-agg-${key}`,
        symbol: info.underlying,
        originalSymbol: formattedLabel,
        quantity: Math.max(1, units / 100),
        price: 0,
        tradeDate: null,
        closingDate: expirationDate.toISOString().substring(0,10),
        cost: 0,
        proceeds: 0,
        commission: 0,
        fees: 0,
        realizedPL: realized,
        netPL: realized,
        tradeType: 'AGG',
        account: acctRef ? acctRef.account : null,
        accountId: acctRef ? acctRef.accountId : null,
        orderId: null,
        positionId: null,
        transactionType: 'AGG',
        externalId: `AGG-CONTRACT-${key}`,
        _isClientSynthAgg: true,
        _closedSide: sideToClose
      };
      synthExpAggregates.push(aggRow);
    }

    if (synthExpAggregates.length > 0) {
      rows.push(...synthExpAggregates);
    }
  } catch (e) {
    console.warn('Client synth expiration pass failed:', e);
  }

  // Client-side aggregate synthesis for option contracts when server AGG rows are absent
  try {
    const isAggRow = (r) => String(r.tradeType || r.trade_type || r.transactionType || r.transaction_type || '').toUpperCase() === 'AGG';
    const normalizeSymbolForParsing = (s) => (s || '').replace(/\s+CONTRACT$/, '');
    const makeContractKey = (r) => {
      const info = parseOptionSymbol(normalizeSymbolForParsing((r.originalSymbol || r.original_symbol || r.symbol || '')));
      if (info && info.isOption && info.expiration && info.type && info.strike != null) {
        return `${info.underlying}|${info.expiration}|${info.type}|${info.strike}|${accountKey(r)}`;
      }
      return null;
    };

    // If there are already aggregated rows, skip synthesis.
    // Only count server-provided AGG rows — the client-synthesized exp-agg rows
    // pushed above are also tradeType 'AGG' and must not suppress this pass.
    const hasAnyAggregates = rows.some(r => isAggRow(r) && !r._isClientSynthAgg);
    if (!hasAnyAggregates) {
      // Group option rows by contract key
      const groups = new Map();
      for (const r of rows) {
        if (r && r._isClientSynthAgg) continue;
        const key = makeContractKey(r);
        if (!key) continue;
        const info = parseOptionSymbol(normalizeSymbolForParsing((r.originalSymbol || r.original_symbol || r.symbol || '')));
        const e = String(r.positionEffect || '').toUpperCase();
        const tt = String(r.tradeType || '').toUpperCase();
        const tx = String(r.transactionType || '').toUpperCase();
        const isOption = !!info.isOption;
        if (!isOption) continue;
        const bucket = groups.get(key) || { rows: [], info };
        bucket.rows.push(r);
        groups.set(key, bucket);
      }

      const synthAggregates = [];
      for (const [key, bucket] of groups.entries()) {
        const { rows: grpRows, info } = bucket;
        if (!grpRows || grpRows.length === 0) continue;
        // Determine if group appears closed: has any explicit close or expiry/assignment, or realizedPL computed > 0 on any row
        const hasCloseEvent = grpRows.some(r => {
          const pe = String(r.positionEffect || '').toUpperCase();
          const tt = String(r.tradeType || '').toUpperCase();
          const tx = String(r.transactionType || '').toUpperCase();
          const realized = parseFloat(r.realizedPL || '');
          return pe.includes('BUY TO CLOSE') || pe.includes('SELL TO CLOSE') || tt === 'BTC' || tt === 'STC' || tx === 'OEXP' || tx === 'OASGN' || (!isNaN(realized) && Math.abs(realized) > 0);
        });
        if (!hasCloseEvent) continue;

        // Compute aggregate amounts
        let totalProceeds = 0;
        let totalCost = 0;
        let totalFees = 0;
        let totalCommission = 0;
        let totalContracts = 0;
        let totalRealizedFromRows = 0;
        let totalDividendFromRows = 0;
        let openDate = null;
        let closeDate = null;
        for (const r of grpRows) {
          if (!r || typeof r !== 'object') continue;
          const qty = Math.abs(parseFloat(r.quantity || 0)) || 0;
          const fee = Math.abs(parseFloat(r.fees || 0)) || 0;
          const comm = Math.abs(parseFloat(r.commission || 0)) || 0;
          const div = getRowDividend(r);
          totalFees += fee;
          totalCommission += comm;
          totalDividendFromRows += div;
          const e = String(r.positionEffect || '').toUpperCase();
          const tt = String(r.tradeType || '').toUpperCase();
          const tx = String(r.transactionType || '').toUpperCase();
          const isOpening = e.includes('SELL TO OPEN') || e.includes('BUY TO OPEN') || tt === 'SELL' || tt === 'BTO' || tt === 'BUY';
          const tradeDt = r.tradeDate ? new Date(r.tradeDate) : null;
          const closeDt = r.closingDate ? new Date(r.closingDate) : null;
          if (isOpening && tradeDt) {
            if (!openDate || tradeDt < openDate) openDate = tradeDt;
          }
          if (!isOpening) {
            const cd = closeDt || tradeDt;
            if (cd && (!closeDate || cd > closeDate)) closeDate = cd;
          }
          if (qty > totalContracts) {
            totalContracts = qty;
          }
          const rowRealized = getClosedTradeNetPL(r);
          if (!isNaN(rowRealized)) {
            totalRealizedFromRows += rowRealized;
          }
          // Aggregate proceeds/cost using fields as-is
          totalProceeds += Math.abs(parseFloat(r.proceeds || 0)) || 0;
          totalCost += Math.abs(parseFloat(r.cost || 0)) || 0;
        }

        const realized = Math.abs(totalRealizedFromRows) > 0.01
          ? totalRealizedFromRows
          : (totalProceeds - totalCost - totalFees - totalCommission);
        const netPL = realized + totalDividendFromRows;
        const formattedLabel = formatOptionLabel({ originalSymbol: info.originalSymbol, symbol: info.originalSymbol });
        const aggRow = {
          id: `client-agg-${key}`,
          symbol: info.underlying,
          originalSymbol: formattedLabel,
          quantity: totalContracts || 1,
          price: 0,
          tradeDate: openDate ? openDate.toISOString().substring(0,10) : (grpRows[0].tradeDate || null),
          closingDate: closeDate ? closeDate.toISOString().substring(0,10) : null,
          cost: totalCost,
          proceeds: totalProceeds,
          commission: totalCommission,
          fees: totalFees,
          dividend: totalDividendFromRows,
          realizedPL: realized,
          netPL: netPL,
          tradeType: 'AGG',
          account: grpRows[0].account,
          accountId: grpRows[0].accountId,
          orderId: null,
          positionId: null,
          transactionType: 'AGG',
          externalId: `AGG-CONTRACT-${key}`,
          _isClientSynthAgg: true
        };

        // Hide individual closed legs for this contract
        for (const r of grpRows) {
          if (!r || typeof r !== 'object') continue;
          try {
            const status = getTradeStatus(r);
            if (status === 'Closed') r._hideDueToAggregation = true;
          } catch (e) {
            console.warn('getTradeStatus failed for row:', r, e);
          }
        }

        synthAggregates.push(aggRow);
      }

      if (synthAggregates.length > 0) {
        rows.push(...synthAggregates);
      }
    }
  } catch (e) {
    console.warn('Client synth aggregate pass failed:', e);
  }

  rows.forEach(r => {
      if (r.realizedPL === undefined && r.realized_pl !== undefined) {
          r.realizedPL = r.realized_pl;
      }

      // Fix up equity realized P/L if missing or reversed
      const info = parseOptionSymbol(r.originalSymbol || r.symbol || "");
      const isOption = !!(info && info.isOption);
      if (!isOption && !r._closedSide) {
          const c = Math.abs(parseFloat(r.cost || 0));
          const p = Math.abs(parseFloat(r.proceeds || 0));
          const div = getRowDividend(r);
          
          if (c > 0.01 && p > 0.01) {
              const calc = (p - c) + div;
              const current = r.realizedPL || 0;
              
              const isReversed = Math.abs(calc + current) < 1.0 && Math.abs(calc) > 1.0;
              const isMissing = Math.abs(current) < 0.01 && Math.abs(calc) > 1.0;
              
              if (isReversed || isMissing) {
                   r.realizedPL = calc;
                   r.netPL = calc;
              }
          }
      }
  });

  return rows;
};

// Fetch all trades with pagination. Dedupes by id — unstable sort keys on
// the server can return the same row on adjacent pages (and skip others).
export const fetchAllTrades = async (baseUrl, chunkSize = 500) => {
  const all = [];
  const seen = new Set();
  let offset = 0;
  let total = null;
  // Loop until total reached (if provided) or batch smaller than chunkSize
  while (true) {
    const sep = baseUrl.includes('?') ? '&' : '?';
    const url = `${baseUrl}${sep}limit=${chunkSize}&offset=${offset}`;
    const resp = await apiRequest(url);
    const data = resp.data || {};
    const batch = data.trades || data || [];
    if (Array.isArray(batch)) {
      batch.forEach(t => {
        if (t && t.id != null) {
          if (seen.has(t.id)) return;
          seen.add(t.id);
        }
        all.push(t);
      });
    }
    const reportedTotal = typeof data.total === 'number' ? data.total : null;
    if (reportedTotal !== null) {
      total = reportedTotal;
    }
    // Stop when we've reached total (if known) or we got fewer than chunkSize
    if ((total !== null && all.length >= total) || (!Array.isArray(batch) || batch.length < chunkSize)) {
      break;
    }
    offset += chunkSize;
  }
  return all;
};

// Get underlying symbol (handles options and class tickers)
export const getUnderlyingSymbol = (input) => {
  // Handle string input or object input
  const raw = typeof input === 'string' 
    ? input 
    : String(input?.originalSymbol || input?.symbol || '').trim();
    
  const info = parseOptionSymbol(raw);
  if (info?.isOption) {
    // For options, rely on parseOptionSymbol which already preserves class tickers (e.g., "BRK B")
    return info.underlying || '';
  }
  // For equities, preserve class designators like "BRK B" consistently.
  // Normalize underscores to spaces, collapse multiple spaces, and keep first two tokens
  const normalized = raw.replace(/_/g, ' ').trim();
  const tokens = normalized.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2 && /^[A-Z]$/.test(tokens[1])) {
    return `${tokens[0]} ${tokens[1]}`;
  }
  return tokens[0] || normalized || raw;
};

// Helper: determine if a trade is a closing transaction
export const isClosingTrade = (trade) => {
  const tt = (String(trade.tradeType || '')).toUpperCase().trim();
  if (!tt) return false;
  // Explicit close markers
  if (tt.includes('TO CLOSE') || tt.includes('STC') || tt.includes('BTC') || tt.includes('CLOSE')) {
    return true;
  }
  // Exclude explicit open markers
  if (tt.includes('TO OPEN') || tt.includes('BTO') || tt.includes('STO')) {
    return false;
  }
  const info = parseOptionSymbol(trade.originalSymbol || trade.symbol || '');
  // Treat any SELL* as a closing action (e.g., SELL, SELL LONG) unless explicitly marked as opening above
  if (tt.startsWith('SELL')) return true;
  // Treat EXPIRATION as closing
  if (tt.includes('EXP')) return true;
  // For short positions, close can be a simple BUY (rarely without markers). Be conservative.
  return false;
};

// Net P/L of a closed trade EXCLUDING any dividend baked into net_pl.
// Prefers netPL/net_pl (fee-adjusted); falls back to realizedPL - fees when no
// net value exists. Some broker rows store net_pl = realized - fees + dividend
// while also carrying the dividend in the row's dividend field — when detected,
// the dividend is stripped so callers that add dividends separately do not
// double count. Dividend-type rows contribute 0 to closed P/L.
export const getClosedTradeNetPL = (trade) => {
  if (!trade) return 0;
  const tt = String(trade.tradeType || trade.trade_type || trade.transactionType || trade.transaction_type || '').toUpperCase();
  if (tt === 'DIVIDEND' || tt === 'DIV') return 0;
  const num = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };
  const net = num(trade.netPL ?? trade.net_pl);
  const gross = num(trade.realizedPL ?? trade.realized_pl ?? trade._realizedSynth);
  const div = num(trade.dividend ?? trade.dividend_amount) || 0;
  const fees = Math.abs(num(trade.commission) || 0) + Math.abs(num(trade.fees) || 0);
  if (net !== null) {
    if (div !== 0 && gross !== null &&
        Math.abs(net - (gross - fees + div)) < Math.abs(net - (gross - fees))) {
      return net - div;
    }
    return net;
  }
  return gross !== null ? gross - fees : 0;
};

// Helper: get realized value (net of commissions/fees — see getClosedTradeNetPL)
export const getRealizedValue = (trade) => getClosedTradeNetPL(trade);

// Classify a group of trades (sharing the same orderId) into a strategy type
export const classifyStrategy = (trades) => {
  // Check for dividend trades first
  const hasDividend = trades.some(t => {
    const tt = String(t.tradeType || t.trade_type || '').toLowerCase();
    const div = parseFloat(t.dividend || t.dividend_amount || 0);
    return tt === 'dividend' || tt === 'div' || div !== 0;
  });
  if (hasDividend) return 'Dividend';

  // Filter to opening legs only for classification
  const openingLegs = trades.filter(t => {
    const tt = String(t.tradeType || t.trade_type || '').toUpperCase();
    const pe = String(t.positionEffect || t.position_effect || '').toUpperCase();
    return tt === 'BTO' || tt === 'STO' || tt === 'BUY_TO_OPEN' || tt === 'SELL_TO_OPEN' ||
      tt === 'BUY TO OPEN' || tt === 'SELL TO OPEN' ||
      (pe.includes('OPENING') && !pe.includes('CLOS'));
  });

  // If no explicit opening legs, check for closing legs and infer direction
  const closingLegs = trades.filter(t => {
    const tt = String(t.tradeType || t.trade_type || '').toUpperCase();
    const pe = String(t.positionEffect || t.position_effect || '').toUpperCase();
    return tt === 'STC' || tt === 'BTC' || tt === 'SELL_TO_CLOSE' || tt === 'BUY_TO_CLOSE' ||
      tt === 'SELL TO CLOSE' || tt === 'BUY TO CLOSE' ||
      (pe.includes('CLOSING') && !pe.includes('OPEN'));
  });

  // Use opening legs if available; otherwise use closing legs with inverted direction
  const useClosing = openingLegs.length === 0 && closingLegs.length > 0;
  const legs = openingLegs.length > 0 ? openingLegs : (useClosing ? closingLegs : trades);
  const rawLegs = legs.map(t => {
    const raw = t.originalSymbol || t.symbol || '';
    const info = parseOptionSymbol(raw);
    const tt = String(t.tradeType || t.trade_type || '').toUpperCase();
    const qty = parseFloat(t.quantity || 0);
    let isShort;
    if (useClosing) {
      isShort = tt === 'BTC' || tt === 'BUY_TO_CLOSE' || tt === 'BUY TO CLOSE';
    } else {
      isShort = tt === 'STO' || tt === 'SELL_TO_OPEN' || tt === 'SELL TO OPEN' ||
        (tt === 'SELL' && qty < 0) || qty < 0;
    }
    return { ...info, isShort, strike: parseFloat(info.strike) || 0, qty };
  }).filter(l => l.isOption);

  // Merge legs with same strike + type + direction (partial fills of same contract)
  const mergedMap = new Map();
  rawLegs.forEach(l => {
    const key = `${l.type}:${l.strike}:${l.isShort}`;
    if (!mergedMap.has(key)) {
      mergedMap.set(key, { ...l });
    } else {
      mergedMap.get(key).qty += l.qty;
    }
  });
  const optionLegs = Array.from(mergedMap.values());

  if (optionLegs.length === 0) return 'Equity';
  if (optionLegs.length === 1) {
    const leg = optionLegs[0];
    const isCall = leg.type === 'C';
    if (leg.isShort) {
      return isCall ? 'Short Call' : 'Short Put';
    }
    return isCall ? 'Long Call' : 'Long Put';
  }

  const calls = optionLegs.filter(l => l.type === 'C').sort((a, b) => a.strike - b.strike);
  const puts = optionLegs.filter(l => l.type === 'P').sort((a, b) => b.strike - a.strike);

  if (calls.length === 2 && puts.length === 0) {
    const [low, high] = calls;
    if (low.isShort && !high.isShort) return 'Call Credit Spread';
    if (!low.isShort && high.isShort) return 'Call Debit Spread';
    if (low.isShort && high.isShort) return 'Short Call Spread';
    return 'Long Call Spread';
  }
  if (puts.length === 2 && calls.length === 0) {
    const [high, low] = puts;
    if (high.isShort && !low.isShort) return 'Put Credit Spread';
    if (!high.isShort && low.isShort) return 'Put Debit Spread';
    if (high.isShort && low.isShort) return 'Short Put Spread';
    return 'Long Put Spread';
  }
  if (calls.length === 1 && puts.length === 1) {
    const call = calls[0];
    const put = puts[0];
    if (call.isShort && put.isShort) {
      return call.strike === put.strike ? 'Short Straddle' : 'Short Strangle';
    }
    if (!call.isShort && !put.isShort) {
      return call.strike === put.strike ? 'Long Straddle' : 'Long Strangle';
    }
    return 'Combo';
  }
  if (calls.length === 2 && puts.length === 2) {
    const putStrikes = puts.map(p => p.strike).sort((a, b) => a - b);
    const callStrikes = calls.map(c => c.strike).sort((a, b) => a - b);
    const innerPut = Math.max(...putStrikes);
    const innerCall = Math.min(...callStrikes);
    if (innerPut === innerCall) return 'Iron Butterfly';
    return 'Iron Condor';
  }
  return `${optionLegs.length}-Leg Option`;
};

// Group trades by orderId to form strategy groups
export const groupTradesByStrategy = (trades) => {
  // Brokers may batch the close of several positions' shared contract into a
  // single fill stamped with one order id. FIFO lot attribution (_lotAlloc,
  // recorded during reconcileTradesForDisplay) knows which opening order each
  // portion actually closed — split the fill back out so each part groups
  // with the position it belonged to.
  const expanded = [];
  for (const t of trades || []) {
    const alloc = t._lotAlloc;
    const orders = alloc ? [...new Set(alloc.map(a => String(a.orderId)))] : [];
    // Only split bracket-style fills — the fill's own order must be one of
    // the allocated orders, meaning open and close share the order id. For
    // brokers whose closes carry fresh unrelated ids the allocated orders
    // wouldn't line up with close groups and the split would scatter fills.
    const ownOrder = String(t.orderId || t.order_id || '');
    if (!alloc || alloc.length < 2 || orders.length < 2 || !orders.includes(ownOrder)) {
      expanded.push(t);
      continue;
    }
    const sign = Math.sign(parseFloat(t.quantity) || 1);
    alloc.forEach((a, i) => {
      expanded.push({
        ...t,
        quantity: sign * (a.qty || 0),
        orderId: a.orderId ?? t.orderId ?? t.order_id,
        realizedPL: a.realized,
        netPL: a.realized,
        _lotSplit: i,
      });
    });
  }
  trades = expanded;

  const stratMap = new Map();
  const acctOf = (trade) => String(trade.accountId ?? trade.account_id ?? trade.account ?? '');

  trades.forEach(trade => {
    const orderId = trade.orderId || trade.order_id;
    const raw = trade.originalSymbol || trade.symbol || '';
    const info = parseOptionSymbol(raw);
    const underlying = info.isOption ? info.underlying : (trade.symbol || 'UNKNOWN');
    const openDate = (trade.openDate || trade.tradeDate || '').slice(0, 10);
    const key = orderId ? `ord:${acctOf(trade)}:${orderId}` : `solo:${underlying}:${openDate}:${trade.id}${trade._lotSplit != null ? ':' + trade._lotSplit : ''}`;

    if (!stratMap.has(key)) {
      stratMap.set(key, { key, orderId, underlying, trades: [] });
    }
    stratMap.get(key).trades.push(trade);
  });

  // Post-grouping pass: merge solo option groups that share the same underlying,
  // expiration, type (C/P), and open date into combined spread groups.
  // This handles cases where broker didn't assign a shared orderId to spread legs.
  const soloGroups = [];
  const ordGroups = [];
  stratMap.forEach(group => {
    if (group.key.startsWith('solo:')) {
      const allOpts = group.trades.every(t => {
        const info = parseOptionSymbol(t.originalSymbol || t.symbol || '');
        return info.isOption;
      });
      if (allOpts && group.trades.length === 1) {
        const info = parseOptionSymbol(group.trades[0].originalSymbol || group.trades[0].symbol || '');
        soloGroups.push({
          ...group,
          _underlying: info.underlying,
          _expiration: info.expiration,
          _type: info.type,
          _openDate: (group.trades[0].openDate || group.trades[0].tradeDate || '').slice(0, 10),
        });
      } else {
        ordGroups.push(group);
      }
    } else {
      ordGroups.push(group);
    }
  });

  // Group solo trades by underlying + expiration + type + openDate + abs(qty)
  // Including abs(qty) separates multiple spreads on same underlying/expiration/date
  // that have different contract quantities.
  // Only merge when there's a mix of short and long legs — same-direction trades
  // (e.g., two short calls) are separate positions, not a spread.
  const spreadMap = new Map();
  soloGroups.forEach(sg => {
    const trade = sg.trades[0];
    const absQty = Math.abs(parseFloat(trade.quantity || 0) || 0);
    const tt = String(trade.tradeType || trade.trade_type || '').toUpperCase();
    const isShort = tt === 'STO' || tt === 'SELL_TO_OPEN' || tt === 'SELL TO OPEN' ||
      (tt === 'SELL' && parseFloat(trade.quantity || 0) < 0) || parseFloat(trade.quantity || 0) < 0;
    const spreadKey = `${sg._underlying}:${sg._expiration}:${sg._type}:${sg._openDate}:${absQty}:${acctOf(trade)}`;
    if (!spreadMap.has(spreadKey)) {
      spreadMap.set(spreadKey, { key: `spread:${spreadKey}`, orderId: null, underlying: sg._underlying, trades: [], _hasShort: false, _hasLong: false });
    }
    const entry = spreadMap.get(spreadKey);
    entry.trades.push(...sg.trades);
    if (isShort) entry._hasShort = true; else entry._hasLong = true;
  });

  // Build final groups: orderId groups + merged spread groups
  const finalGroups = [...ordGroups];
  spreadMap.forEach(group => {
    const isSpread = group.trades.length > 1 && group._hasShort && group._hasLong;
    if (isSpread) {
      // Multi-leg spread with opposite directions: classify as spread
      finalGroups.push(group);
    } else {
      // Not a spread — emit each trade as its own solo group
      group.trades.forEach(trade => {
        const info = parseOptionSymbol(trade.originalSymbol || trade.symbol || '');
        const underlying = info.isOption ? info.underlying : (trade.symbol || 'UNKNOWN');
        const openDate = (trade.openDate || trade.tradeDate || '').slice(0, 10);
        finalGroups.push({
          key: `solo:${underlying}:${openDate}:${trade.id}`,
          orderId: null,
          underlying,
          trades: [trade],
        });
      });
    }
  });

  return finalGroups.map(group => {
    let strategyType = classifyStrategy(group.trades);
    // Futures-option groups get the same 'Futures ' prefix used elsewhere so
    // the 'Futures Option' strategy filter and labels stay consistent.
    const isFuturesOption = group.trades.length > 0 && group.trades.every(t => {
      const info = parseOptionSymbol(t.originalSymbol || t.symbol || '');
      return info.isOption && info.isFuturesOption;
    });
    if (isFuturesOption && strategyType !== 'Equity' && !/^futures/i.test(strategyType)) {
      strategyType = `Futures ${strategyType}`;
    }
    const totalPL = group.trades.reduce((s, t) => s + getClosedTradeNetPL(t), 0);
    const totalDividend = group.trades.reduce((s, t) => s + parseFloat(t.dividend || t.dividend_amount || 0), 0);
    // Position size = max per-contract quantity across the group. Opening legs
    // can be missing entirely (FIFO hides a fully-consumed opening row) or split
    // into multiple fills, so neither trades[0] nor a single opening leg is
    // reliable — sum quantities per contract and take the largest leg.
    const qtyByContract = { open: new Map(), close: new Map() };
    group.trades.forEach(t => {
      const tt = String(t.tradeType || t.trade_type || '').toUpperCase();
      const ttN = tt.replace(/_/g, ' ');
      const key = String(t.originalSymbol || t.symbol || '').trim();
      const q = Math.abs(parseFloat(t.quantity) || 0);
      if (tt === 'BTO' || tt === 'STO' || ttN === 'BUY TO OPEN' || ttN === 'SELL TO OPEN' || tt === 'BUY')
        qtyByContract.open.set(key, (qtyByContract.open.get(key) || 0) + q);
      else if (tt === 'STC' || tt === 'BTC' || ttN === 'SELL TO CLOSE' || ttN === 'BUY TO CLOSE' || tt === 'SELL')
        qtyByContract.close.set(key, (qtyByContract.close.get(key) || 0) + q);
    });
    const qtySrc = qtyByContract.open.size ? qtyByContract.open : qtyByContract.close;
    const quantity = qtySrc.size
      ? Math.max(...qtySrc.values())
      : Math.abs(parseFloat(group.trades[0].quantity) || 0);
    let price;
    if (group.trades.length === 1) {
      price = parseFloat(group.trades[0].price) || 0;
    } else {
      price = group.trades.reduce((s, t) => {
        const tt = String(t.tradeType || t.trade_type || '').toUpperCase();
        const p = parseFloat(t.price) || 0;
        const isShort = tt === 'STO' || tt === 'SELL_TO_OPEN' || tt === 'SELL TO OPEN' || tt === 'STC' || tt === 'SELL_TO_CLOSE' || tt === 'SELL TO CLOSE';
        return s + (isShort ? p : -p);
      }, 0);
    }
    const openDate = group.trades.reduce((earliest, t) => {
      const od = (t.openDate || t.tradeDate || '').slice(0, 10);
      return od && (!earliest || od < earliest) ? od : earliest;
    }, null);
    const closeDate = group.trades.reduce((latest, t) => {
      const tt = String(t.tradeType || t.trade_type || '').toUpperCase();
      const ttNorm = tt.replace(/_/g, ' ');
      const pe = String(t.positionEffect || t.position_effect || '').toUpperCase().replace(/_/g, ' ');
      const isClosingLeg = tt === 'STC' || tt === 'BTC' || ttNorm === 'BUY TO CLOSE' || ttNorm === 'SELL TO CLOSE' ||
        pe.includes('BUY TO CLOSE') || pe.includes('SELL TO CLOSE');
      const cd = (t.closingDate || (isClosingLeg ? t.tradeDate : '') || '').slice(0, 10);
      return cd && (!latest || cd > latest) ? cd : latest;
    }, null);
    const holdDays = openDate && closeDate
      ? Math.round((new Date(closeDate + 'T00:00:00') - new Date(openDate + 'T00:00:00')) / 86400000)
      : 0;
    const isMultiLeg = group.trades.length > 1;
    const displaySymbol = isMultiLeg ? group.underlying : (group.trades[0].originalSymbol || group.trades[0].symbol || group.underlying);
    const legStrikes = isMultiLeg ? multiLegStrikes(group.trades) : [];
    return { ...group, strategyType, isFuturesOption, totalPL, totalDividend, quantity, price, isMultiLeg, displaySymbol, legStrikes, openDate, closeDate, holdDays };
  }).sort((a, b) => {
    const ac = a.closeDate || '';
    const bc = b.closeDate || '';
    if (ac !== bc) return bc.localeCompare(ac);
    return (b.openDate || '').localeCompare(a.openDate || '');
  });
};

// Shared aggregation function for closed positions P/L calculation
// This implements the same logic as StockSummary's pivot table aggregation
export const aggregateClosedPositions = (trades, options = {}) => {
  const {
    groupBy = 'month', // 'day', 'week', 'month', 'year'
    startDate,
    endDate,
    accountIds,
    positionFilter // 'all', 'equity', 'options', 'dividends'
  } = options;

  // Helper to get period key from date
  const getPeriodKeyFromDate = (dateObj) => {
    const y = dateObj.getUTCFullYear();
    const m = dateObj.getUTCMonth();
    const d = dateObj.getUTCDate();
    const base = new Date(Date.UTC(y, m, d));

    if (groupBy === 'week') {
      const dayNum = base.getUTCDay() || 7;
      const isoThursday = new Date(base);
      isoThursday.setUTCDate(base.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(isoThursday.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil(((isoThursday - yearStart) / 86400000 + 1) / 7);
      return `${isoThursday.getUTCFullYear()}-${String(weekNo).padStart(2, '0')}`;
    }
    if (groupBy === 'month') {
      return `${base.getUTCFullYear()}-${String(base.getUTCMonth() + 1).padStart(2, '0')}`;
    }
    if (groupBy === 'year') {
      return `${base.getUTCFullYear()}`;
    }
    return base.toISOString().slice(0, 10);
  };

  // Helper to get period key from trade
  const getPeriodKey = (trade) => {
    const tradeDate = trade.closingDate || trade.tradeDate || trade.trade_date;
    if (!tradeDate) return null;
    const date = new Date(tradeDate);
    return getPeriodKeyFromDate(date);
  };

  // Helper to get realized value for pivot (net of commissions/fees)
  const getRealizedValueForPivot = (trade) => getClosedTradeNetPL(trade);

  // Helper to get dividend value
  const getDividendValue = (trade) => {
    const div = trade.dividend || trade.dividend_amount || 0;
    return parseFloat(div) || 0;
  };

  // Filter trades by account
  let filteredTrades = trades || [];
  if (accountIds && accountIds.length > 0 && !accountIds.includes('all')) {
    filteredTrades = filteredTrades.filter(t => {
      const accountId = String(t.accountId || t.account_id || '');
      const selectedAccountIds = accountIds.map(id => String(id));
      return selectedAccountIds.includes(accountId);
    });
  }

  // Filter trades by position type (equity, options, dividends)
  if (positionFilter && !positionFilter.includes('all')) {
    filteredTrades = filteredTrades.filter(trade => {
      const tradeType = (trade.tradeType || trade.trade_type || '').toLowerCase();
      const dividend = trade.dividend || trade.dividend_amount || 0;
      const isDividend = tradeType === 'dividend' || dividend !== 0;
      const raw = trade.originalSymbol || trade.symbol || '';
      const info = parseOptionSymbol(raw);
      const isOption = info && info.isOption;
      const isStock = !isOption && !isDividend;

      // If dividends are not selected, exclude all dividend trades
      if (!positionFilter.includes('dividends') && isDividend) {
        return false;
      }

      const matchesOption = positionFilter.includes('options') && isOption;
      const matchesStock = positionFilter.includes('equity') && isStock;

      return matchesOption || matchesStock;
    });
  }

  // Filter by date range
  if (startDate) {
    filteredTrades = filteredTrades.filter(t => {
      const tradeDate = t.closingDate || t.tradeDate || t.trade_date;
      return tradeDate && tradeDate >= startDate;
    });
  }

  if (endDate) {
    filteredTrades = filteredTrades.filter(t => {
      const tradeDate = t.closingDate || t.tradeDate || t.trade_date;
      return tradeDate && tradeDate <= endDate;
    });
  }

  // Build pivot with P/L calculations
  const pivotMap = new Map();
  const colSet = new Set();
  const tradeIdSet = new Set(); // Track trade IDs to prevent duplicates

  filteredTrades.forEach(trade => {
    const symbol = getUnderlyingSymbol(trade);
    if (!symbol) return;

    // Skip AGG (synthesized aggregate) trades as they're not actual user trades
    if (trade.tradeType === 'AGG') return;

    // Use trade ID or combination of fields to deduplicate
    const tradeId = trade.id || trade.external_id || `${trade.symbol}_${trade.tradeDate}_${trade.quantity}_${trade.price}`;
    if (tradeIdSet.has(tradeId)) return;
    tradeIdSet.add(tradeId);

    const periodKey = getPeriodKey(trade);
    if (!periodKey) return;

    const realized = getRealizedValueForPivot(trade);
    const dividend = getDividendValue(trade);

    colSet.add(periodKey);
    if (!pivotMap.has(symbol)) {
      pivotMap.set(symbol, {
        symbol,
        periodTotals: {},
        totalPL: 0,
        totalDividendOnly: 0,
        trades: []
      });
    }
    const row = pivotMap.get(symbol);
    row.periodTotals[periodKey] = (row.periodTotals[periodKey] || 0) + realized + dividend;
    row.totalPL += (realized + dividend);
    row.totalDividendOnly += dividend;
    row.trades.push(trade);
  });

  // Convert map to array
  const symbolData = Array.from(pivotMap.values());

  // Calculate totals row
  const totalsRow = {
    symbol: 'Total',
    periodTotals: {},
    totalPL: 0,
    totalDividendOnly: 0
  };

  symbolData.forEach(row => {
    totalsRow.totalPL += row.totalPL;
    totalsRow.totalDividendOnly += (row.totalDividendOnly || 0);
    Object.keys(row.periodTotals || {}).forEach(col => {
      totalsRow.periodTotals[col] = (totalsRow.periodTotals[col] || 0) + row.periodTotals[col];
    });
  });

  // Generate period columns in chronological order
  const columnsChrono = Array.from(colSet.values()).sort();

  return {
    symbolData,
    totalsRow,
    periodColumns: columnsChrono
  };
};

// ==== Shared filter helpers (Dashboard + StockSummary must stay identical) ====

// Parse symbolFilter into include and exclude terms.
// Supports minus-prefixed tokens to exclude symbols, e.g. "-TSLA -AAPL".
export const parseSymbolFilter = (filter) => {
  const tokens = String(filter || '').trim().split(/\s+/).filter(Boolean);
  const include = [];
  const exclude = [];
  for (const t of tokens) {
    if (t.startsWith('-') && t.length > 1) exclude.push(t.slice(1).toLowerCase());
    else include.push(t.toLowerCase());
  }
  return { include, exclude };
};

// Tags carried directly on an entity or anything nested under it
// (trades, legs, position).
export const getEntityTags = (entity) => {
  const tags = new Set();
  const add = (e) => {
    if (!e) return;
    (Array.isArray(e.tags) ? e.tags : []).forEach(t => { if (t) tags.add(t); });
    (Array.isArray(e.trades) ? e.trades : []).forEach(add);
    (Array.isArray(e.legs) ? e.legs : []).forEach(add);
    if (e.position) add(e.position);
  };
  add(entity);
  return [...tags];
};

// Full tag set: entity's own/nested tags + a lookup callback applied to the
// entity, its legs, and its position (e.g. open-trade tag map for live rows).
export const collectEntityTags = (entity, tagLookup) => {
  const tags = new Set(getEntityTags(entity));
  const addLookup = (e) => {
    if (!e) return;
    if (tagLookup) (tagLookup(e) || []).forEach(t => tags.add(t));
    (Array.isArray(e.legs) ? e.legs : []).forEach(addLookup);
    if (e.position) addLookup(e.position);
  };
  addLookup(entity);
  return [...tags];
};

// Tag filter test: include = union match (any selected tag present);
// exclude = reject when any excluded tag is present. A tag in both lists
// excludes.
export const entityMatchesTagFilter = (entity, tagFilter, tagFilterExclude, tagLookup) => {
  const hasInc = tagFilter && tagFilter.length > 0;
  const hasExc = tagFilterExclude && tagFilterExclude.length > 0;
  if (!hasInc && !hasExc) return true;
  const tags = collectEntityTags(entity, tagLookup);
  if (hasExc && tagFilterExclude.some(t => tags.includes(t))) return false;
  if (hasInc && !tagFilter.some(t => tags.includes(t))) return false;
  return true;
};

// Does a strategyType satisfy the current strategyFilter value?
export const strategyTypeMatchesFilter = (strategyType, strategyFilter) => {
  if (!strategyFilter || strategyFilter === 'all') return true;
  if (strategyFilter === 'Equity') return strategyType === 'Equity';
  if (strategyFilter === 'Dividend') return strategyType === 'Dividend';
  if (strategyFilter === 'Futures Option') return /^futures/i.test(strategyType || '');
  if (['Short Call', 'Long Call', 'Short Put', 'Long Put'].includes(strategyFilter)) {
    return strategyType === strategyFilter;
  }
  return (strategyType || '').includes(strategyFilter);
};

// Does a single trade satisfy the position-type filter (options/stocks/dividends)?
// `bothSymbols` is an optional Set of uppercased underlying symbols that have
// BOTH an open stock position and an open option position — used by the
// 'options-stocks' filter value.
export const tradeMatchesPositionType = (trade, positionFilter, bothSymbols) => {
  if (!positionFilter || positionFilter.includes('all')) return true;
  const tradeType = (trade.tradeType || trade.trade_type || '').toLowerCase();
  const dividend = trade.dividend || trade.dividend_amount || 0;
  const isDividend = tradeType === 'dividend' || dividend !== 0;
  const raw = trade.originalSymbol || trade.symbol || '';
  const info = parseOptionSymbol(raw);
  const isOption = info && info.isOption;
  const isStock = !isOption && !isDividend;
  const matchesOption = positionFilter.includes('options') && isOption;
  const matchesStock = positionFilter.includes('stocks') && isStock;
  const matchesDividend = positionFilter.includes('dividends') && isDividend;
  const matchesBoth = positionFilter.includes('options-stocks') && !!bothSymbols &&
    bothSymbols.has(String(getUnderlyingSymbol(trade) || raw).toUpperCase());
  return matchesOption || matchesStock || matchesDividend || matchesBoth;
};

// Row-level closed-position filter — shared by StockSummary and Dashboard so
// both pages produce identical totals for identical filter selections.
// rows: [{ symbol, trades, ... }] (pivot rows keyed by underlying).
// opts: { symbolFilter, strategyFilter, openDateStart, openDateEnd,
//         positionFilter, tagFilter, tagFilterExclude, groupBy, tagLookup }
// Returns rows with trades narrowed to matching trades and recomputed
// totalPL / totalDividendOnly / periodTotals.
// Bank cash-flow rows imported from Plaid depository (checking/savings)
// accounts carry transaction_type 'plaid_bank_*'. They are income records,
// not positions — never surface them as closed positions or dividends.
export const isBankCashFlowRow = (trade) => {
  const tx = String(trade?.transactionType || trade?.transaction_type || '').toLowerCase();
  return tx.startsWith('plaid_bank_');
};

export const filterClosedPositionRows = (rows, opts = {}) => {
  const {
    symbolFilter = '',
    strategyFilter = 'all',
    openDateStart = '',
    openDateEnd = '',
    positionFilter = ['all'],
    tagFilter = [],
    tagFilterExclude = [],
    groupBy = 'month',
    tagLookup = null,
    bothSymbols = null, // Set of underlying symbols with both stock+option positions
  } = opts;

  const { include: incTerms, exclude: excTerms } = parseSymbolFilter(symbolFilter);
  const hasStrategyFilter = strategyFilter && strategyFilter !== 'all';
  const hasOpenDateFilter = openDateStart || openDateEnd;
  const hasPositionFilter = positionFilter && !positionFilter.includes('all');
  const hasTagFilter = (tagFilter && tagFilter.length > 0) || (tagFilterExclude && tagFilterExclude.length > 0);

  const matchesTags = (entity) =>
    entityMatchesTagFilter(entity, tagFilter, tagFilterExclude, tagLookup);

  const getPeriodKeyForDate = (td) => {
    if (!td) return null;
    if (groupBy === 'week') {
      const date = new Date(td);
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
      return `${d.getUTCFullYear()}-${String(weekNo).padStart(2, '0')}`;
    }
    if (groupBy === 'month') return String(td).slice(0, 7);
    if (groupBy === 'quarter') {
      const date = new Date(td);
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const q = Math.floor(d.getUTCMonth() / 3) + 1;
      return `${d.getUTCFullYear()}-Q${q}`;
    }
    if (groupBy === 'year') return String(td).slice(0, 4);
    return String(td).slice(0, 10);
  };

  return (rows || []).filter(row => {
    // Position type filter: exclude rows with no matching trades
    if (hasPositionFilter) {
      const hasMatch = (row.trades || []).some(t => tradeMatchesPositionType(t, positionFilter, bothSymbols));
      if (!hasMatch) return false;
    }
    // Symbol filter (include / exclude)
    if (incTerms.length > 0 || excTerms.length > 0) {
      const sym = (row.symbol || '').toLowerCase();
      if (excTerms.some(t => sym.includes(t))) return false;
      if (incTerms.length > 0 && !incTerms.some(t => sym.includes(t))) return false;
    }

    // Tag filter is applied per strategy during narrowing below — a tagged
    // strategy must not hide the row's untagged strategies (e.g. a tagged
    // stock sale on a symbol that also holds option spreads). The row dies
    // only when every strategy inside it fails the tag check.
    if (hasTagFilter) {
      const strategies = groupTradesByStrategy(row.trades || []);
      if (strategies.length > 0 && !strategies.some(s => matchesTags(s))) return false;
      if (strategies.length === 0 && !matchesTags(row)) return false;
    }

    // Strategy filter: check if any strategy group for this symbol matches
    if (hasStrategyFilter) {
      const strategies = groupTradesByStrategy(row.trades || []);
      const hasMatch = strategies.some(s => strategyTypeMatchesFilter(s.strategyType, strategyFilter));
      if (!hasMatch) return false;
    }

    // Open date range filter
    if (hasOpenDateFilter) {
      const hasMatch = (row.trades || []).some(t => {
        const openDate = (t.openDate || t.tradeDate || '').slice(0, 10);
        if (!openDate) return true;
        if (openDateStart && openDate < openDateStart) return false;
        if (openDateEnd && openDate > openDateEnd) return false;
        return true;
      });
      if (!hasMatch) return false;
    }

    return true;
  }).map(row => {
    // Filter trades within each row by position type
    let filteredRowTrades = row.trades || [];
    if (hasPositionFilter) {
      filteredRowTrades = filteredRowTrades.filter(t => tradeMatchesPositionType(t, positionFilter, bothSymbols));
    }
    const filteredRow = { ...row, trades: filteredRowTrades };

    if (!hasStrategyFilter && !hasOpenDateFilter && !hasPositionFilter && !hasTagFilter) return filteredRow;

    const tradeKey = (t) => t.id || t.external_id || `${t.symbol}_${t.tradeDate}_${t.quantity}_${t.price}`;

    // Build set of matching trade IDs from strategy filter
    let matchingTradeIds = null;
    if (hasStrategyFilter) {
      const strategies = groupTradesByStrategy(filteredRowTrades || []);
      const matchingStrategies = strategies.filter(s => strategyTypeMatchesFilter(s.strategyType, strategyFilter));
      matchingTradeIds = new Set();
      matchingStrategies.forEach(s => {
        s.trades.forEach(t => matchingTradeIds.add(tradeKey(t)));
      });
    }

    // Same narrowing for the tag filter: keep whole strategies whose tag
    // union intersects the selection so untagged legs of a tagged strategy
    // still render.
    let tagMatchingTradeIds = null;
    if (hasTagFilter) {
      const strategies = groupTradesByStrategy(filteredRowTrades || []);
      tagMatchingTradeIds = new Set();
      strategies.filter(s => matchesTags(s)).forEach(s => {
        s.trades.forEach(t => tagMatchingTradeIds.add(tradeKey(t)));
      });
    }

    const matchingTrades = (filteredRowTrades || []).filter(t => {
      if (hasStrategyFilter && !matchingTradeIds.has(tradeKey(t))) return false;
      if (hasTagFilter && !tagMatchingTradeIds.has(tradeKey(t))) return false;
      if (hasOpenDateFilter) {
        const openDate = (t.openDate || t.tradeDate || '').slice(0, 10);
        if (openDate) {
          if (openDateStart && openDate < openDateStart) return false;
          if (openDateEnd && openDate > openDateEnd) return false;
        }
      }
      return true;
    });

    // Recompute periodTotals, totalPL, totalDividendOnly from matching trades
    const periodTotals = {};
    let totalPL = 0;
    let totalDividendOnly = 0;

    matchingTrades.forEach(trade => {
      const realized = getClosedTradeNetPL(trade);
      const dividend = parseFloat(trade.dividend || trade.dividend_amount || 0) || 0;

      const td = trade.closingDate || trade.tradeDate;
      const periodKey = getPeriodKeyForDate(td);
      if (periodKey) {
        periodTotals[periodKey] = (periodTotals[periodKey] || 0) + realized + dividend;
      }
      totalPL += realized + dividend;
      totalDividendOnly += dividend;
    });

    return { ...filteredRow, totalPL, totalDividendOnly, periodTotals, trades: matchingTrades };
  });
};

// Wash sale detection (IRS Pub 550): a loss sale is a wash when substantially
// identical securities are acquired within 30 days before or after the sale
// while still held — or are acquired after it.
// "Substantially identical" for options means the SAME contract (underlying +
// expiration + type + strike) — rolling into a different week/strike is not a
// wash, matching broker 1099-B behavior. For stock losses, a same-symbol buy
// or buying a call option (contract to buy) counts as replacement.
// Lot matching uses timestamp-ordered FIFO so a buy that IS the closed lot is
// never counted as its own replacement. Returns a Map keyed by loss-trade id
// -> { disallowedLoss, lossQty, replacedQty, replacements[] }.
export const detectWashSales = (trades, options = {}) => {
  const { isTaxableAccount = () => true } = options;
  const DAY_MS = 86400000;
  const WINDOW = 30 * DAY_MS;
  const result = new Map();

  const normType = (t) => String(t.tradeType || t.trade_type || '').toUpperCase().replace(/[\s-]+/g, '_');
  const openLong = (t) => ['BUY', 'BTO', 'BUY_TO_OPEN'].includes(normType(t));
  const openShort = (t) => ['STO', 'SELL_TO_OPEN'].includes(normType(t));
  const closeLong = (t) => ['SELL', 'STC', 'SELL_TO_CLOSE'].includes(normType(t));
  const closeShort = (t) => ['BTC', 'BUY_TO_CLOSE'].includes(normType(t));
  const closeAny = (t) => ['ASSIGNMENT', 'OASGN', 'OEXP', 'EXPIRE'].includes(normType(t));
  const isClosing = (t) => closeLong(t) || closeShort(t) || closeAny(t);
  const isAcquisition = (t) => openLong(t) || openShort(t);

  const infoOf = (t) => parseOptionSymbol(t.originalSymbol || t.original_symbol || t.symbol || '');
  const underlyingOf = (t) => {
    const info = infoOf(t);
    return String(info.isOption ? info.underlying : (t.symbol || info.underlying || '')).toUpperCase();
  };
  // Identity of the actual security: contract key for options, ticker for stock
  const contractKey = (t) => {
    const info = infoOf(t);
    if (!info.isOption) return `EQ:${underlyingOf(t)}`;
    return `OPT:${info.underlying}|${info.expiration}|${info.type}|${info.strike}`;
  };
  const realizedOf = (t) => {
    const raw = t.netPL ?? t.net_pl ?? t.realizedPL ?? t.realized_pl ?? t._realizedSynth;
    if (raw === undefined || raw === null || raw === '' || isNaN(parseFloat(raw))) return null;
    return getClosedTradeNetPL(t);
  };
  const tsOf = (t) => {
    const ts = t.tradeTimestamp || t.trade_timestamp;
    if (ts) return new Date(ts).getTime();
    return new Date(t.tradeDate || t.trade_date || 0).getTime();
  };
  const closeDateOf = (t) => new Date(t.closingDate || t.closing_date || t.tradeDate || t.trade_date || 0).getTime();

  // Drop re-import duplicates: a row with no execution timestamp that exactly
  // matches a timestamped row is the same fill imported twice.
  const kOf = (t) => [t.accountId || t.account_id, t.symbol, t.originalSymbol || t.original_symbol || '',
                      t.tradeDate || t.trade_date, t.quantity, normType(t)].join('|');
  const tsKeyed = new Map();
  (trades || []).forEach(t => {
    if (t.trade_timestamp || t.tradeTimestamp) tsKeyed.set(kOf(t), (tsKeyed.get(kOf(t)) || 0) + 1);
  });
  const clean = (trades || []).filter(t => {
    if (t.trade_timestamp || t.tradeTimestamp) return true;
    const n = tsKeyed.get(kOf(t)) || 0;
    if (n > 0) { tsKeyed.set(kOf(t), n - 1); return false; }
    return true;
  });

  // Bucket every trade by underlying symbol
  const byUnderlying = new Map();
  clean.forEach(t => {
    const u = underlyingOf(t);
    if (!u) return;
    if (!byUnderlying.has(u)) byUnderlying.set(u, []);
    byUnderlying.get(u).push(t);
  });

  byUnderlying.forEach(list => {
    // FIFO lot matching per contract: walk executions in timestamp order,
    // tracking which close consumed which acquisition lot.
    const longLots = new Map();  // contractKey -> [{qty, acq}]
    const shortLots = new Map();
    // Timestamp order; same-timestamp ties put openings before closings so a
    // same-day round trip is treated as its own lot, not a replacement.
    const ordRank = (t) => (isAcquisition(t) ? 0 : 1);
    const sorted = [...list].sort((a, b) => (tsOf(a) - tsOf(b)) || (ordRank(a) - ordRank(b)) || ((a.id || 0) - (b.id || 0)));
    const closes = [];
    const acqs = [];

    // Lot queues are scoped by account: a close can only consume lots held in
    // the same account. contractKey itself stays unscoped so wash-sale
    // replacement matching still detects repurchases across accounts.
    const lotKey = (t) => `${t.accountId || t.account_id || ''}|${contractKey(t)}`;
    const consume = (queueMap, t, qty) => {
      const key = lotKey(t);
      const q = queueMap.get(key) || [];
      let need = qty;
      for (const lot of q) {
        if (need <= 0) break;
        if (lot.remaining <= 0) continue;
        const used = Math.min(lot.remaining, need);
        lot.remaining -= used;
        lot.consumed.push({ closeId: t.id, date: closeDateOf(t), qty: used });
        need -= used;
      }
      queueMap.set(key, q);
    };

    sorted.forEach(t => {
      const qty = Math.abs(parseFloat(t.quantity) || 0);
      if (qty <= 0) return;
      const info = infoOf(t);
      if (openLong(t) || openShort(t)) {
        const acq = {
          t, date: tsOf(t), qty, remaining: qty,
          consumed: [], // {closeId, date, qty}
          accountId: t.accountId || t.account_id,
          isOption: info.isOption, key: contractKey(t), info,
        };
        acqs.push(acq);
        const qm = openLong(t) ? longLots : shortLots;
        const key = lotKey(t);
        if (!qm.has(key)) qm.set(key, []);
        qm.get(key).push(acq);
      } else if (isClosing(t)) {
        if (closeShort(t)) consume(shortLots, t, qty);
        else if (closeLong(t)) consume(longLots, t, qty);
        else if (info.isOption) consume(longLots, t, qty); // option expire/assign; equity 'assignment' event rows are paired with separate share fills
        const realized = realizedOf(t);
        if (realized !== null && realized < 0) {
          const accId = t.accountId || t.account_id;
          if (isTaxableAccount(accId)) {
            closes.push({ t, date: closeDateOf(t), qty, loss: -realized, isOption: info.isOption, key: contractKey(t), info });
          }
        }
      }
    });

    // Whether an acquisition can replace the closed position under Pub 550.
    const isReplacement = (c, a) => {
      if (c.isOption) return a.isOption && a.key === c.key;
      if (!a.isOption) return true; // same underlying bucket = same ticker
      return a.info.type === 'C' && openLong(a.t); // contract to buy the stock
    };

    // Quantity of an acquisition lot that was actually held (or later bought)
    // at the moment of the loss sale: excludes the portion consumed by this
    // close itself (that IS the sold lot) and portions already closed earlier.
    const eligibleQty = (a, c) => {
      let excluded = 0;
      a.consumed.forEach(ev => {
        if (ev.closeId === c.t.id || ev.date <= c.date) excluded += ev.qty;
      });
      return a.qty - excluded;
    };

    closes.sort((a, b) => a.date - b.date);
    const allocated = new Map(); // acq -> qty already allocated as replacement

    closes.forEach(c => {
      let remainingLoss = c.qty;
      const replacements = [];
      acqs.forEach(a => {
        if (remainingLoss <= 0) return;
        if (Math.abs(a.date - c.date) > WINDOW) return;
        if (!isReplacement(c, a)) return;
        const free = eligibleQty(a, c) - (allocated.get(a) || 0);
        if (free <= 0) return;
        const used = Math.min(free, remainingLoss);
        allocated.set(a, (allocated.get(a) || 0) + used);
        remainingLoss -= used;
        replacements.push({ trade: a.t, qty: used, inIra: !isTaxableAccount(a.accountId) });
      });
      if (replacements.length > 0) {
        const replacedQty = c.qty - remainingLoss;
        const disallowedLoss = c.loss * (replacedQty / c.qty);
        result.set(c.t.id, {
          disallowedLoss: Math.round(disallowedLoss * 100) / 100,
          lossQty: c.qty,
          replacedQty,
          replacements,
        });
      }
    });
  });

  return result;
};
